import "./LegalPage.css";

const LAST_UPDATED = "June 2025";

export default function ShippingDeliveryPolicy() {
    return (
        <div className="legal-page">
            <div className="legal-container">

                <div className="legal-header">
                    <div className="legal-eyebrow">Legal</div>
                    <h1 className="legal-title">Shipping & Delivery Policy</h1>
                    <p className="legal-meta">Last updated: {LAST_UPDATED} · Applies to all Learnlia products and services</p>
                </div>

                <div className="legal-notice legal-notice--ok">
                    <strong>Summary:</strong> Learnlia is a 100% digital platform. There is nothing to ship.
                    All products — PRO subscriptions, certificates, and course access — are delivered instantly
                    online. No physical goods are ever sold or dispatched.
                </div>

                <div className="legal-body">

                    <section className="legal-section">
                        <h2>1. Nature of Our Products</h2>
                        <p>
                            Learnlia is an online learning platform. All products and services offered by Learnlia are
                            entirely digital in nature. We do not sell, manufacture, or dispatch any physical goods,
                            merchandise, printed materials, or tangible items of any kind.
                        </p>
                        <p>
                            Our products include:
                        </p>
                        <ul>
                            <li>
                                <strong>Learnlia PRO Subscription</strong> — digital access to premium learning tracks,
                                AI-powered quizzes, and advanced analytics, delivered instantly upon successful payment.
                            </li>
                            <li>
                                <strong>Verified Digital Certificates</strong> — PDF certificates issued digitally upon
                                course completion, accessible from your profile and downloadable at any time.
                            </li>
                            <li>
                                <strong>Learning Tracks & Course Content</strong> — structured curriculum delivered
                                entirely through our web platform, accessible from any internet-connected device.
                            </li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>2. Delivery of Digital Products</h2>

                        <h3>PRO Subscription Access</h3>
                        <p>
                            PRO access is activated <strong>immediately</strong> upon successful payment confirmation
                            from our payment processors (Razorpay or Paddle). You do not need to take any action —
                            your account is upgraded automatically. If your access is not reflected within 5 minutes
                            of payment, please refresh your browser or log out and back in. If the issue persists,
                            contact us at <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a>.
                        </p>

                        <h3>Digital Certificates</h3>
                        <p>
                            Certificates are generated and made available within your Learnlia profile immediately
                            upon course completion. You can download your certificate as a PDF, share it via a
                            unique public URL, or add it to your LinkedIn profile directly from your profile page.
                            Certificates do not expire and are available as long as your account exists.
                        </p>

                        <h3>Course & Track Content</h3>
                        <p>
                            All learning content is available on-demand through the Learnlia web platform. There are
                            no download delays, shipping queues, or waiting periods. Free tier content is available
                            immediately upon account creation. PRO tier content becomes accessible immediately upon
                            PRO activation.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>3. Access Requirements</h2>
                        <p>To access Learnlia's digital products, you need:</p>
                        <ul>
                            <li>A registered Learnlia account with a verified email address.</li>
                            <li>An internet connection (broadband or mobile data).</li>
                            <li>A supported browser (Chrome, Firefox, Safari, or Edge — latest two versions).</li>
                            <li>For video content: a minimum connection speed of 5 Mbps is recommended.</li>
                        </ul>
                        <p>
                            Learnlia is accessible from any country with unrestricted internet access. We do not
                            region-lock content. However, payment availability may vary by country depending on
                            supported payment processors.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>4. No Physical Shipping</h2>
                        <p>
                            Since Learnlia delivers only digital products:
                        </p>
                        <ul>
                            <li>No shipping address is required for any purchase.</li>
                            <li>No delivery timeframes, courier services, or tracking numbers apply.</li>
                            <li>No import duties, customs fees, or taxes related to physical goods apply.</li>
                            <li>There are no shipping costs, handling charges, or packaging fees.</li>
                        </ul>
                        <p>
                            This policy is provided in compliance with Indian e-commerce regulations and payment
                            gateway requirements that mandate a shipping and delivery disclosure for digital goods.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>5. Delivery Failures & Technical Issues</h2>
                        <p>
                            In rare cases, digital delivery may be delayed due to:
                        </p>
                        <ul>
                            <li>Payment processor delays in confirming payment status (typically under 5 minutes).</li>
                            <li>Scheduled platform maintenance (announced in advance via email and our status page).</li>
                            <li>Temporary technical issues on our servers.</li>
                        </ul>
                        <p>
                            If your PRO access, certificate, or course content is not delivered within 15 minutes of
                            a successful payment, please contact us immediately at{" "}
                            <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a> with
                            your payment confirmation details. We will resolve access issues within 2 hours during
                            business hours (Mon–Sat, 9 AM – 6 PM IST).
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>6. Service Availability</h2>
                        <p>
                            Learnlia targets 99.5% uptime. Planned maintenance windows will be communicated at least
                            24 hours in advance via email to active subscribers. Unplanned outages will be communicated
                            via our official social media channels and resolved as quickly as possible.
                            Outages do not constitute grounds for refunds unless they extend beyond 72 consecutive hours.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Changes to This Policy</h2>
                        <p>
                            We may update this policy from time to time as our product offerings evolve. Any changes
                            will be reflected on this page with a revised "Last updated" date. If Learnlia ever introduces
                            physical products in the future, a separate, detailed shipping policy for those products will be published.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Contact Us</h2>
                        <p>For any questions about digital delivery or access issues:</p>
                        <div className="legal-contact-block">
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Email</span>
                                <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a>
                            </div>
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Response time</span>
                                <span>Within 2 hours for access issues · 1 business day for general queries</span>
                            </div>
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Business hours</span>
                                <span>Monday – Saturday, 9:00 AM – 6:00 PM IST</span>
                            </div>
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Registered as</span>
                                <span>Learnlia (India) · Tamil Nadu, India</span>
                            </div>
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
}